import type { Account } from '@/types/v2/account';
import axios from '@/lib/v2/axios';
import type { User } from '@/types/v2/user';

export async function getAccount(accountId: string): Promise<Account> {
  const response = await axios.get(`/api/v2/accounts/${accountId}`);
  return response.data;
}

export async function getAccounts(): Promise<{ accounts: Account[] }> {
  const response = await axios.get('/api/v2/accounts?include=owner&sort=-created_at');
  return response.data;
}

export async function getAdministrators(accountId: string): Promise<{ administrators: User[] }> {
  const response = await axios.get(`/api/v2/accounts/${accountId}/administrators`);
  return response.data;
}

export async function getAdministrator(accountId: string, administratorId: string): Promise<{ administrator: User }> {
  const response = await axios.get(`/api/v2/accounts/${accountId}/administrators/${administratorId}`);
  return response.data;
}

// System Users
export async function getSystemUsers(
  accountId: string,
  params?: { archived?: boolean; page?: number; per_page?: number; search?: string }
): Promise<{ systemUsers: User[]; meta?: { per_page: number; current_page: number; has_more: boolean; total: number; total_pages: number } }> {
  const queryParams: Record<string, unknown> = {
    include: 'communities,units',
  };
  if (params?.archived) {
    queryParams.archived = 1;
  }
  if (params?.page) {
    queryParams.page = params.page;
  }
  if (params?.per_page) {
    queryParams.per_page = params.per_page;
  }
  if (params?.search?.trim()) {
    queryParams.search = params.search.trim();
  }

  const response = await axios.get(`/api/v2/accounts/${accountId}/system-users`, {
    params: queryParams,
  });
  const raw = response.data;
  if (Array.isArray(raw)) {
    return { systemUsers: raw };
  }
  if (Array.isArray(raw?.systemUsers)) {
    return raw;
  }
  if (Array.isArray(raw?.users)) {
    return { ...raw, systemUsers: raw.users };
  }
  if (Array.isArray(raw?.data)) {
    return { ...raw, systemUsers: raw.data };
  }
  return { ...raw, systemUsers: [] };
}

export async function getAllSystemUsers(
  accountId: string,
  params?: { archived?: boolean; search?: string }
): Promise<{ systemUsers: User[]; meta?: { per_page: number; current_page: number; has_more: boolean; total: number; total_pages: number } }> {
  const firstPage = await getSystemUsers(accountId, {
    archived: params?.archived,
    search: params?.search,
    page: 1,
    per_page: 100,
  });
  const allUsers = [...firstPage.systemUsers];
  const totalPages = firstPage.meta?.total_pages ?? 1;

  for (let page = 2; page <= totalPages; page += 1) {
    const nextPage = await getSystemUsers(accountId, {
      archived: params?.archived,
      search: params?.search,
      page,
      per_page: 100,
    });
    allUsers.push(...nextPage.systemUsers);
  }

  return { ...firstPage, systemUsers: allUsers };
}

export async function getSystemUser(accountId: string, userId: string): Promise<{ systemUser: User }> {
  const response = await axios.get(`/api/v2/accounts/${accountId}/system-users/${userId}`);
  return response.data;
}

export async function updateUserAccountRole(
  accountId: string,
  userId: string,
  data: { role_name: string | null }
): Promise<void> {
  await axios.patch(`/api/v2/accounts/${accountId}/system-users/${userId}/account-role`, data);
}

export async function addUserToCommunity(
  accountId: string,
  userId: string,
  data: { community_id: number | string; role_name: string; user_title_id?: string }
): Promise<void> {
  await axios.post(`/api/v2/accounts/${accountId}/system-users/${userId}/communities`, data);
}

export async function updateUserCommunityRole(
  accountId: string,
  userId: string,
  communityId: string,
  data: { role_name: string }
): Promise<void> {
  await axios.patch(`/api/v2/accounts/${accountId}/system-users/${userId}/communities/${communityId}/role`, data);
}

export async function removeUserFromCommunity(
  accountId: string,
  userId: string,
  communityId: string
): Promise<void> {
  await axios.delete(`/api/v2/accounts/${accountId}/system-users/${userId}/communities/${communityId}`);
}

export async function addUserToUnit(
  accountId: string,
  userId: string,
  data: {
    unit_id: string;
    role_name: string;
    user_title_id?: string;
    move_in_date?: string;
    move_out_date?: string;
  }
): Promise<void> {
  await axios.post(`/api/v2/accounts/${accountId}/system-users/${userId}/units`, data);
}

export async function removeUserFromUnit(
  accountId: string,
  userId: string,
  unitId: string
): Promise<void> {
  await axios.delete(`/api/v2/accounts/${accountId}/system-users/${userId}/units/${unitId}`);
}

/** Archive a system user (soft delete). Data is preserved, they no longer appear in lists. */
export async function archiveSystemUser(
  accountId: string,
  userId: string
): Promise<void> {
  await axios.post(`/api/v2/accounts/${accountId}/system-users/${userId}/archive`);
}

/** Restore an archived system user. They will reappear in active lists. */
export async function restoreSystemUser(
  accountId: string,
  userId: string
): Promise<void> {
  await axios.post(`/api/v2/accounts/${accountId}/system-users/${userId}/restore`);
}
